import React, { useState } from 'react';
import { Image, X, Gift } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../../contexts/AuthContext';
import { db, storage } from '../../lib/firebase';
import { collection, addDoc, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { GifPicker } from './GifPicker';

import { vibrate, HapticPatterns } from '../../utils/haptics';

export default function CreatePost({ onPostCreated, variant = 'classic' }: { onPostCreated?: () => void, variant?: 'classic' | 'modern' }) {
  const { user } = useAuth();
  const [newPost, setNewPost] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [selectedGif, setSelectedGif] = useState<string | null>(null);



  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/*': [],
      'video/*': []
    },
    onDrop: acceptedFiles => {
      setSelectedFiles(prev => [...prev, ...acceptedFiles]);
    },
    maxFiles: 4
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || (!newPost.trim() && selectedFiles.length === 0 && !selectedGif) || loading) return;

    setLoading(true);

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.exists() ? userDoc.data() : null;
      const username = userData?.username || user.email?.split('@')[0] || 'Anonymous';

      const mediaUrls: string[] = [];
      const mediaTypes: string[] = [];

      // Add GIF to mediaUrls if selected
      if (selectedGif) {
        mediaUrls.push(selectedGif);
        mediaTypes.push('gif');
      }

      // Upload other media files
      for (const file of selectedFiles) {
        try {
          const metadata = {
            contentType: file.type,
            customMetadata: {
              'Access-Control-Allow-Origin': '*'
            }
          };
          
          const storageRef = ref(storage, `posts/${user.uid}/${Date.now()}_${file.name}`);
          await uploadBytes(storageRef, file, metadata);
          const url = await getDownloadURL(storageRef);
          mediaUrls.push(url);
          mediaTypes.push(file.type.startsWith('image/') ? 'image' : 'video');
        } catch (error: any) {
          console.error('Error uploading file:', error);
          throw new Error(`Failed to upload ${file.name}: ${error.message || 'Unknown error'}`);
        }
      }

      await addDoc(collection(db, 'posts'), {
        content: newPost.trim(),
        authorId: user.uid,
        authorName: username,
        authorPhoto: user.photoURL || '',
        mediaUrls: mediaUrls.length > 0 ? mediaUrls : [],
        mediaTypes: mediaTypes.length > 0 ? mediaTypes : [],
        likes: 0,
        comments: 0,
        shares: 0,
        likedBy: [],
        reactions: {
          like: { count: 0, userIds: [] },
          laugh: { count: 0, userIds: [] },
          fire: { count: 0, userIds: [] },
          clap: { count: 0, userIds: [] },
          thinking: { count: 0, userIds: [] }
        },
        createdAt: serverTimestamp()
      });

      setNewPost('');
      setSelectedFiles([]);
      setSelectedGif(null);
      vibrate(HapticPatterns.SUCCESS);
      onPostCreated?.(); // Call the callback after successful post creation
    } catch (err) {
      console.error('Error creating post:', err);
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'modern') {
    return (
      <div className="bg-white dark:bg-navy-800 rounded-[32px] shadow-[0_2px_20px_rgb(0,0,0,0.04)] dark:shadow-none border border-gray-100 dark:border-navy-700 p-6 sm:p-8 mb-8">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-start gap-4">
            {user?.photoURL ? (
              <img
                src={user.photoURL}
                alt="Profile"
                className="w-12 h-12 rounded-full object-cover flex-shrink-0 ring-2 ring-white dark:ring-navy-800 shadow-sm"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-orange-400 to-orange-500 flex items-center justify-center text-white font-bold text-lg ring-2 ring-white dark:ring-navy-800 shadow-sm">
                {user?.email?.[0].toUpperCase() || 'A'}
              </div>
            )}
            <textarea
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
              placeholder="What's your latest bit?"
              className="w-full bg-transparent border-0 px-2 py-3 text-2xl text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-navy-600 focus:outline-none resize-none transition-all font-medium tracking-tight"
              rows={newPost.length > 50 ? 3 : 1}
            />
          </div>

          <div className="flex justify-between items-center pt-2">
            <div className="flex items-center gap-2 pl-14">
              <button
                type="button"
                className="flex items-center justify-center w-10 h-10 text-gray-400 hover:text-orange-500 hover:bg-orange-50 dark:hover:bg-navy-700 rounded-full transition-all duration-200"
                {...getRootProps()}
              >
                <Image className="w-5 h-5 stroke-[2]" />
                <input {...getInputProps()} />
              </button>
              <button
                type="button"
                className="flex items-center justify-center w-10 h-10 text-gray-400 hover:text-orange-500 hover:bg-orange-50 dark:hover:bg-navy-700 rounded-full transition-all duration-200"
                onClick={() => setShowGifPicker(true)}
              >
                <Gift className="w-5 h-5 stroke-[2]" />
              </button>
            </div>
            <button
              type="submit"
              disabled={loading || (!newPost.trim() && selectedFiles.length === 0 && !selectedGif)}
              className="px-6 py-2.5 bg-gray-900 dark:bg-orange-500 text-white font-bold rounded-full hover:bg-gray-800 dark:hover:bg-orange-600 active:scale-95 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              {loading ? 'Posting...' : 'Post'}
            </button>
          </div>

          {showGifPicker && (
            <GifPicker
              onGifSelect={(gifUrl) => {
                setSelectedGif(gifUrl);
                setShowGifPicker(false);
              }}
              onClose={() => setShowGifPicker(false)}
            />
          )}

          {selectedGif && (
            <div className="relative mt-4 ml-16 max-w-md">
              <img src={selectedGif} alt="Selected GIF" className="w-full h-auto rounded-2xl object-contain max-h-[400px]" />
              <button
                onClick={() => setSelectedGif(null)}
                className="absolute top-3 right-3 p-1.5 bg-gray-900/50 backdrop-blur-sm rounded-full text-white hover:bg-gray-900/80 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {selectedFiles.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-4 ml-16">
              {selectedFiles.map((file, index) => (
                <div key={index} className="relative">
                  <img
                    src={URL.createObjectURL(file)}
                    alt="Preview"
                    className="h-28 w-28 object-cover rounded-2xl"
                  />
                  <button
                    onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                    className="absolute top-2 right-2 bg-gray-900/50 backdrop-blur-sm text-white rounded-full p-1.5 hover:bg-gray-900/80 transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </form>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6 mb-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-start gap-4">
          <img
            src={user?.photoURL || '/default-avatar.png'}
            alt="Profile"
            className="w-12 h-12 rounded-full object-cover flex-shrink-0 ring-2 ring-gray-200"
          />
          <textarea
            value={newPost}
            onChange={(e) => setNewPost(e.target.value)}
            placeholder="What's on your mind?"
            className="w-full bg-gray-50 border-0 rounded-xl px-4 py-3 text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:bg-white resize-none transition-all"
            rows={3}
          />
        </div>

        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex items-center text-gray-500 hover:text-orange-500 hover:bg-orange-50 px-3 py-2 rounded-lg transition-all duration-200"
              {...getRootProps()}
            >
              <Image className="w-5 h-5 mr-1" />
              <span className="text-sm font-medium">Gallery</span>
              <input {...getInputProps()} />
            </button>
            <button
              type="button"
              className="flex items-center text-gray-500 hover:text-orange-500 hover:bg-orange-50 px-3 py-2 rounded-lg transition-all duration-200"
              onClick={() => setShowGifPicker(true)}
            >
              <Gift className="w-5 h-5 mr-1" />
              <span className="text-sm font-medium">GIF</span>
            </button>
          </div>
          <button
            type="submit"
            disabled={loading || (!newPost.trim() && selectedFiles.length === 0 && !selectedGif)}
            className="px-6 py-2.5 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 hover:shadow-md active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none disabled:active:scale-100"
          >
            {loading ? 'Posting...' : 'Post'}
          </button>
        </div>

        {showGifPicker && (
          <GifPicker
            onGifSelect={(gifUrl) => {
              setSelectedGif(gifUrl);
              setShowGifPicker(false);
            }}
            onClose={() => setShowGifPicker(false)}
          />
        )}

        {selectedGif && (
          <div className="relative mt-2 max-w-md">
            <img src={selectedGif} alt="Selected GIF" className="w-full h-auto rounded-lg object-contain max-h-[400px]" />
            <button
              onClick={() => setSelectedGif(null)}
              className="absolute top-2 right-2 p-1 bg-gray-800 bg-opacity-50 rounded-full text-white hover:bg-opacity-70"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {selectedFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {selectedFiles.map((file, index) => (
              <div key={index} className="relative">
                <img
                  src={URL.createObjectURL(file)}
                  alt="Preview"
                  className="h-24 w-24 object-cover rounded-lg"
                />
                <button
                  onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </form>
    </div>
  );
}